import { getAssistance, type AssistanceSession } from "./admin-assistance";
import {
  readCachedApiResponse,
  purgeOfflineState,
  getOfflineIdentityScope,
  getOfflineSessionRevision,
  isOfflineSessionCurrent,
  setOfflineIdentity,
  writeCachedApiResponseForScope,
} from "./offline-store";
const inFlightControllers = new Set<AbortController>();

export function cancelInFlightApiRequests() {
  for (const controller of inFlightControllers) {
    controller.abort();
    inFlightControllers.delete(controller);
  }
}

export function invalidateOfflineState() {
  cancelInFlightApiRequests();
  purgeOfflineState();
  setOfflineIdentity(null);
  clearElevationGrant();
}

export type UserRole =
  | "admin"
  | "support"
  | "provider_owner"
  | "provider_staff"
  | "customer";

export type ProviderApprovalStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "suspended";

export type VenueApprovalStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "removal_requested"
  | "removed";

export type ProviderMembershipRole = "owner" | "manager" | "front_desk";

export interface AuthUser {
  id: string;
  email: string;
  display_name: string | null;
  role: UserRole;
}

export interface SessionResponse {
  user: AuthUser;
  home_path: string;
  booking_draft_id?: string | null;
}

export interface AuthStatusResponse {
  authenticated: boolean;
  user: AuthUser | null;
  home_path: string | null;
}

export interface VerificationPendingResponse {
  status: "verification_pending";
  email: string;
}

export interface WorkspaceSummary {
  eyebrow: string;
  title: string;
  description: string;
  checklist: string[];
  provider_name?: string | null;
  provider_status?: ProviderApprovalStatus | null;
  provider_review_note?: string | null;
  membership_role?: ProviderMembershipRole | null;
}

export type UnitType =
  | "console"
  | "pc"
  | "vr"
  | "simulator"
  | "billiards"
  | "pingpong"
  | "room"
  | "other";

export type UnitStatus =
  | "available"
  | "occupied"
  | "reserved"
  | "blocked"
  | "offline";

export type RoomActivity = "console" | "pc" | "vr" | "simulator";
export type RoomAmenity = "screen" | "netflix" | "cinema";

export interface ProviderAccountSummary {
  id: string;
  name: string;
}

export interface ProviderPeakPricingWindow {
  id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  hourly_rate: number;
}

export interface ProviderUnit {
  id: string;
  name: string;
  type: UnitType;
  status: UnitStatus;
  base_status: UnitStatus;
  base_hourly_rate: number;
  capacity: number;
  is_vip: boolean;
  supported_activities?: RoomActivity[] | null;
  amenities?: RoomAmenity[] | null;
  capability_revision?: number;
  photos: ProviderMediaPhoto[];
  peak_pricing_windows: ProviderPeakPricingWindow[];
  current_session: ProviderCurrentSession | null;
}

export interface VenueOperatingHoursEntry {
  day_of_week: number;
  is_open: boolean;
  open_time: string | null;
  close_time: string | null;
}

export interface ProviderBlackoutWindow {
  id: string;
  start_at: string;
  end_at: string;
  note: string | null;
}

export interface ProviderVenue {
  id: string;
  name: string;
  city: string;
  timezone: string;
  address: string;
  image_url: string | null;
  logo_url: string | null;
  latitude: number | null;
  longitude: number | null;
  approval_status: VenueApprovalStatus;
  submission_note: string | null;
  removal_note: string | null;
  review_note: string | null;
  reviewed_at: string | null;
  is_active: boolean;
  photos: ProviderMediaPhoto[];
  operating_hours: VenueOperatingHoursEntry[];
  blackout_windows: ProviderBlackoutWindow[];
  units: ProviderUnit[];
}

export interface ProviderVenuesResponse {
  provider: ProviderAccountSummary;
  venues: ProviderVenue[];
}

export type BookingType = "fixed_time" | "open_time";

export type BookingStatus =
  | "pending"
  | "confirmed"
  | "checked_in"
  | "completed"
  | "cancelled"
  | "no_show"
  | "rejected"
  | "expired";

export interface ProviderBooking {
  customer_user_id?: string | null;
  linked_account_name?: string | null;
  id: string;
  venue_id: string;
  venue_name: string;
  venue_timezone: string;
  unit_id: string;
  unit_name: string;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string | null;
  type: BookingType;
  status: BookingStatus;
  scheduled_start_at: string;
  scheduled_end_at: string;
  scheduled_total_amount: number;
  notes: string | null;
  status_note: string | null;
  admit_qr_payload: string;
  created_at: string;
  updated_at: string;
}

export interface ProviderBookingsResponse {
  bookings: ProviderBooking[];
}

export type CustomerFoodOrderStatus =
  | "pending"
  | "preparing"
  | "ready"
  | "delivered"
  | "cancelled";

export interface CustomerFoodOrderItem {
  id: string;
  food_item_id: string;
  item_name: string;
  quantity: number;
  unit_price: number;
}

export interface CustomerFoodOrder {
  id: string;
  session_id: string;
  venue_id: string;
  status: CustomerFoodOrderStatus;
  notes: string | null;
  total_amount: number;
  items: CustomerFoodOrderItem[];
  created_at: string;
  updated_at: string;
}

export interface CustomerFoodOrdersResponse {
  orders: CustomerFoodOrder[];
}

export interface CustomerBooking {
  id: string;
  venue_id: string;
  venue_name: string;
  venue_timezone: string;
  unit_id: string;
  unit_name: string;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string | null;
  type: BookingType;
  status: BookingStatus;
  scheduled_start_at: string;
  scheduled_end_at: string;
  scheduled_total_amount: number;
  notes: string | null;
  status_note: string | null;
  check_in_qr_payload: string;
  created_at: string;
  updated_at: string;
}

export interface CustomerBookingsResponse {
  bookings: CustomerBooking[];
}

export interface CustomerSession {
  id: string;
  venue_id: string;
  venue_name: string;
  unit_id: string;
  unit_name: string;
  booking_id: string | null;
  source: SessionSource;
  status: SessionStatus;
  started_at: string;
  planned_end_at: string | null;
  ended_at: string | null;
  elapsed_total_amount: number;
  planned_total_amount: number | null;
  duration_minutes: number;
  overtime_minutes: number;
  is_overtime: boolean;
  notification_revision: number;
  created_at: string;
  updated_at: string;
}

export interface CustomerSessionsResponse {
  sessions: CustomerSession[];
}

export interface DeletionRequestListItem {
  id: string;
  user_id?: string | null;
  user_email: string;
  user_display_name: string | null;
  user_role: string;
  status: "pending" | "approved" | "rejected" | "completed";
  reason: string | null;
  admin_note: string | null;
  requested_at: string;
  reviewed_at: string | null;
  completed_at: string | null;
}

export type AccountDeletionRequestStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "completed"
  | "all";

export interface AccountDeletionRequestResponse {
  id: string;
  status: string;
  requested_at: string;
}

export type SessionSource = "booking" | "walk_in";
export type SessionStatus = "active" | "completed" | "cancelled";
export type PaymentMethod = "cash" | "card" | "transfer" | "other";

export interface ProviderCurrentSession {
  id: string;
  booking_id: string | null;
  source: SessionSource;
  status: SessionStatus;
  customer_name: string;
  started_at: string;
  planned_end_at: string | null;
  ended_at: string | null;
  duration_minutes: number;
  overtime_minutes: number;
  is_overtime: boolean;
}

export interface MediaPhoto {
  id: string;
  url: string;
  alt_text: string | null;
  position: number;
  is_primary: boolean;
}

export interface ProviderMediaPhoto extends MediaPhoto {
  content_type: string;
  size_bytes: number;
  created_at: string;
  updated_at: string;
}

export interface ProviderSession {
  id: string;
  venue_id: string;
  venue_name: string;
  unit_id: string;
  unit_name: string;
  booking_id: string | null;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string | null;
  source: SessionSource;
  status: SessionStatus;
  started_at: string;
  planned_end_at: string | null;
  ended_at: string | null;
  elapsed_total_amount: number;
  planned_total_amount: number | null;
  payment_collected: boolean;
  payment_method: PaymentMethod | null;
  payment_recorded_at: string | null;
  payment_note: string | null;
  notes: string | null;
  ended_note: string | null;
  duration_minutes: number;
  overtime_minutes: number;
  is_overtime: boolean;
  notification_revision: number;
  created_at: string;
  updated_at: string;
}

export interface ProviderSessionsResponse {
  sessions: ProviderSession[];
}

/** Provider approval contract from CustomerExtensionQuote / ProviderExtensionRequest. */
export interface ProviderExtensionQuote {
  id: string;
  session_id: string;
  contract_version: 1 | 2;
  additional_minutes: number;
  expected_notification_revision: number;
  current_end_at: string | null;
  proposed_end_at: string;
  current_total_amount: number;
  additional_amount: number;
  proposed_total_amount: number;
  currency: string;
  venue_timezone: string;
  expires_at: string;
  approval_deadline: string | null;
  status: "pending" | "applied" | "expired" | "rejected";
  phase: "preview" | "pending_approval" | "applied" | "rejected" | "cancelled" | "expired" | "stale";
  requested_at: string | null;
  decided_at: string | null;
  decision: "approved" | "rejected" | "cancelled" | "expired" | "stale" | null;
  decided_by: string | null;
  rejection_reason: string | null;
  applied_at: string | null;
}

export interface ProviderExtensionRequest {
  quote: ProviderExtensionQuote;
  session: ProviderSession;
}

export interface ProviderExtensionRequestsResponse {
  requests: ProviderExtensionRequest[];
}

export interface ProviderCustomerHistoryBooking {
  id: string;
  venue_name: string;
  unit_name: string;
  status: BookingStatus;
  scheduled_start_at: string;
  scheduled_end_at: string;
  notes: string | null;
  status_note: string | null;
}

export interface ProviderCustomerHistorySession {
  id: string;
  venue_name: string;
  unit_name: string;
  booking_id: string | null;
  source: SessionSource;
  status: SessionStatus;
  started_at: string;
  planned_end_at: string | null;
  ended_at: string | null;
  duration_minutes: number;
  overtime_minutes: number;
  is_overtime: boolean;
  notes: string | null;
  ended_note: string | null;
}

export interface ProviderCustomerHistoryResponse {
  matched_on: string;
  identifier: string;
  bookings: ProviderCustomerHistoryBooking[];
  sessions: ProviderCustomerHistorySession[];
}

export interface ProviderCustomerDetailReview {
  id: string;
  venue_name: string;
  booking_id: string | null;
  stars: number;
  comment: string | null;
  created_at: string;
}

export interface ProviderCustomerDetailResponse {
  matched_on: string;
  identifier: string;
  bookings: ProviderCustomerHistoryBooking[];
  sessions: ProviderCustomerHistorySession[];
  reviews: ProviderCustomerDetailReview[];
}

export interface ProviderCustomerDetailFilters {
  customerUserId?: string | null;
  customerName?: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;
}

export interface ProviderCustomerHistoryFilters {
  customerUserId?: string | null;
  customerName?: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;
  excludeBookingId?: string | null;
  excludeSessionId?: string | null;
}

export interface ProviderCustomerRecord {
  customer_user_id?: string | null;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string | null;
  booking_count: number;
  last_booking_at: string;
}

export interface ProviderCustomersResponse {
  customers: ProviderCustomerRecord[];
}

export interface ProviderNoteRecord {
  id: string;
  source: string;
  venue_id: string;
  venue_name: string;
  unit_id: string | null;
  unit_name: string | null;
  booking_id: string | null;
  session_id: string | null;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string | null;
  note: string;
  created_at: string;
}

export interface ProviderNotesResponse {
  notes: ProviderNoteRecord[];
}

export interface ProviderNotesFilters {
  venueId?: string;
}

export interface CreateVenueInput {
  name: string;
  city: string;
  timezone: string;
  address: string;
  image_url?: string | null;
  logo_url?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  submission_note?: string | null;
}

export interface VenueLocationCandidate {
  latitude: number;
  longitude: number;
  label: string;
}

export interface VenueLocationResolveResponse {
  source: string;
  results: VenueLocationCandidate[];
}

export interface RequestVenueRemovalInput {
  removal_note: string;
}

export interface CreateUnitPeakPricingWindowInput {
  day_of_week: number;
  start_time: string;
  end_time: string;
  hourly_rate: number;
}

export interface CreateUnitInput {
  name: string;
  type: UnitType;
  status: UnitStatus;
  base_hourly_rate: number;
  capacity: number;
  is_vip: boolean;
  supported_activities?: RoomActivity[] | null;
  amenities?: RoomAmenity[] | null;
  expected_capability_revision?: number;
  peak_pricing_windows: CreateUnitPeakPricingWindowInput[];
}

export interface CreatePresignedUploadInput {
  owner_type: "venue" | "unit" | "game" | "food" | "logo";
  owner_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
}

export interface PresignedUploadResponse {
  upload_url: string;
  public_url: string;
  storage_key: string;
  content_type: string;
  max_file_size_bytes: number;
  expires_in_seconds: number;
  headers: Record<string, string>;
}

export interface CreateProviderPhotoInput {
  storage_key: string;
  url: string;
  content_type: string;
  size_bytes: number;
  alt_text?: string | null;
  is_primary?: boolean;
}

export interface UpdateProviderPhotoInput {
  alt_text?: string | null;
  is_primary?: boolean | null;
}

export interface CreateProviderBookingInput {
  venue_id: string;
  unit_id: string;
  customer_name: string;
  customer_email?: string | null;
  customer_phone?: string | null;
  scheduled_start_local: string;
  scheduled_end_local: string;
  notes?: string | null;
}

export interface UpdateProviderBookingInput extends CreateProviderBookingInput {}

export interface UpdateProviderBookingStatusInput {
  status: BookingStatus;
  note?: string | null;
}

export interface ProviderBookingFilters {
  venueId?: string;
  unitId?: string;
  statuses?: BookingStatus[];
  scheduledFromLocal?: string;
  scheduledToLocal?: string;
}

export interface ProviderSessionFilters {
  venueId?: string;
  unitId?: string;
  statuses?: SessionStatus[];
}

export interface UpdateVenueAvailabilityInput {
  operating_hours: VenueOperatingHoursEntry[];
}

export interface CreateVenueBlackoutWindowInput {
  start_local: string;
  end_local: string;
  note?: string | null;
}

export interface StartSessionFromBookingInput {
  note?: string | null;
}

export interface StartWalkInSessionInput {
  venue_id: string;
  unit_id: string;
  customer_name?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  planned_duration_minutes: number;
  notes?: string | null;
}

export interface ExtendProviderSessionInput {
  additional_minutes: number;
}

/** Internal request contract for session extensions. UI callers continue to
 * provide only the additional duration; the session hook snapshots the
 * authoritative revision before dispatching this payload. */
export interface ExtendProviderSessionRequest extends ExtendProviderSessionInput {
  expected_notification_revision: number;
}

export interface StopProviderSessionInput {
  note?: string | null;
  payment_collected?: boolean;
  payment_method?: PaymentMethod | null;
  payment_note?: string | null;
}

export interface ProviderTeamMember {
  user_id: string;
  email: string;
  display_name: string | null;
  role: UserRole;
  membership_role?: ProviderMembershipRole | null;
  is_active: boolean;
  venue_ids: string[] | null;
}

export interface ProviderTeamResponse {
  provider: ProviderAccountSummary;
  members: ProviderTeamMember[];
}

export interface CreateProviderStaffInput {
  email: string;
  display_name: string;
  password: string;
  role?: "front_desk" | "manager";
  venue_ids?: string[] | null;
}

export interface UpdateProviderStaffRoleInput {
  role: ProviderMembershipRole;
}

export interface RequestProviderElevationInput {
  approver_email: string;
  approver_password: string;
}

export interface ProviderElevationResponse {
  grant_id: string;
  expires_at: string;
}

export interface UpdateProviderStaffVenuesInput {
  venue_ids: string[] | null;
}

export interface ProviderRegistrationInput {
  business_name: string;
  contact_name: string;
  email: string;
  password: string;
  city: string;
  contact_phone?: string | null;
  application_note?: string | null;
}

export interface ProviderRegistrationResubmissionInput {
  business_name: string;
  contact_name: string;
  email: string;
  city: string;
  contact_phone?: string | null;
  application_note?: string | null;
}

const BOOKING_ADMIT_QR_PREFIX = "gaamed:booking:";

export function buildBookingAdmitQrPayload(bookingId: string) {
  return `${BOOKING_ADMIT_QR_PREFIX}${bookingId}`;
}

export function parseBookingAdmitQrPayload(qrPayload: string) {
  const normalized = qrPayload.trim();
  if (!normalized.startsWith(BOOKING_ADMIT_QR_PREFIX)) {
    throw new Error("QR code is invalid.");
  }

  const bookingId = normalized.slice(BOOKING_ADMIT_QR_PREFIX.length).trim();
  if (!bookingId) {
    throw new Error("QR code is invalid.");
  }

  return bookingId;
}

export interface ProviderRegistrationRecord {
  business_name: string;
  contact_name: string;
  email: string;
  city: string;
  contact_phone: string | null;
  application_note: string | null;
  approval_status: ProviderApprovalStatus;
  review_note: string | null;
}

export interface AdminProviderAccount {
  owner_setup_pending?: boolean;
  id: string;
  business_name: string;
  city: string;
  contact_name: string | null;
  contact_email: string;
  contact_phone: string | null;
  approval_status: ProviderApprovalStatus;
  application_note: string | null;
  review_note: string | null;
  reviewed_at: string | null;
  reviewed_by_name: string | null;
  reviewed_by_email: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProviderOwnerSetupEmailResult {
  status: string;
  code: string;
  retryable: boolean;
  access_locked: boolean;
  sessions_revoked: boolean;
}

export interface CreateAdminProviderResponse extends AdminProviderAccount {
  setup_email: ProviderOwnerSetupEmailResult;
}

export interface AdminProviderAccountsResponse {
  providers: AdminProviderAccount[];
}

export interface AdminProviderMemberSummary {
  user_id: string;
  email: string;
  display_name: string | null;
  role: UserRole;
  membership_role?: "owner" | "manager" | "front_desk" | null;
  is_active: boolean;
  email_verified?: boolean;
  setup_pending?: boolean;
  venue_ids: string[] | null;
}

export interface AdminProviderDetailVenueSummary {
  setup_checks?: Record<string, boolean>;
  id: string;
  name: string;
  city: string;
  approval_status: VenueApprovalStatus;
  is_active: boolean;
  created_at: string;
}

export interface AdminProviderDetail extends AdminProviderAccount {
  active_sessions: number;
  active_venues: number;
  booking_count: number;
  bookings_last_7_days: number;
  members: AdminProviderMemberSummary[];
  venues: AdminProviderDetailVenueSummary[];
}

export interface CreateAdminProviderInput {
  send_setup_email?: boolean;
  business_name: string;
  contact_name: string;
  email: string;
  city: string;
  contact_phone?: string | null;
  application_note?: string | null;
}

export interface UpdateAdminProviderInput {
  business_name: string;
  contact_name: string;
  city: string;
  contact_phone?: string | null;
  application_note?: string | null;
}

export type AdminRejectReason =
  | "policy_violation"
  | "duplicate_account"
  | "incomplete_data"
  | "fraud_risk"
  | "other";

export interface BulkAdminProviderStatusInput {
  provider_ids: string[];
  approval_status: ProviderApprovalStatus;
  reason_code?: AdminRejectReason | null;
  review_note?: string | null;
}

export interface BulkAdminVenueStatusInput {
  venue_ids: string[];
  decision: "approve" | "reject";
  review_note?: string | null;
}

export interface UpdateAdminProviderStatusInput {
  reason_code?: AdminRejectReason | null;
  approval_status: ProviderApprovalStatus;
  review_note?: string | null;
}

export interface AdminVenueReview {
  id: string;
  provider_id: string;
  provider_name: string;
  name: string;
  city: string;
  timezone: string;
  address: string;
  image_url: string | null;
  latitude: number | null;
  longitude: number | null;
  approval_status: VenueApprovalStatus;
  submission_note: string | null;
  removal_note: string | null;
  review_note: string | null;
  reviewed_at: string | null;
  reviewed_by_name: string | null;
  reviewed_by_email: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminVenuesResponse {
  venues: AdminVenueReview[];
}

export type VenueReviewStatus = "pending" | "approved" | "rejected";

export interface AdminReviewRecord {
  id: string;
  venue_id: string;
  venue_name: string;
  customer_user_id: string | null;
  customer_name: string | null;
  customer_email: string | null;
  booking_id: string | null;
  stars: number;
  comment: string | null;
  status: VenueReviewStatus;
  review_note: string | null;
  reviewed_at: string | null;
  reviewed_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminPendingReviewsResponse {
  reviews: AdminReviewRecord[];
}

export interface UpdateAdminReviewStatusInput {
  decision: "approve" | "reject";
  review_note?: string | null;
}

export type ReviewReportResolution = "dismiss" | "remove_review";
export interface AdminReviewReport {
  id: string;
  review_id: string;
  target: "review" | "author";
  reason: string;
  details: string | null;
  status: string;
  resolution_note: string | null;
  evidence_snapshot: Record<string, unknown>;
  created_at: string;
  stale: boolean;
  venue_id: string;
  venue_name: string;
  current_review: {
    status: string;
    stars: number;
    comment: string | null;
    updated_at: string | null;
  } | null;
}

export interface UpdateAdminVenueStatusInput {
  decision: "approve" | "reject";
  review_note?: string | null;
}

export interface AdminAuditEventSummary {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  actor_user_id: string | null;
  actor_name?: string | null;
  provider_id: string | null;
  venue_id: string | null;
  before_json?: Record<string, unknown> | null;
  after_json?: Record<string, unknown> | null;
  created_at: string;
}

export interface AdminAnalytics {
  total_providers: number;
  approved_providers: number;
  pending_provider_registrations: number;
  suspended_providers: number;
  total_venues: number;
  approved_venues: number;
  pending_venue_approvals: number;
  pending_venue_removals: number;
  total_bookings: number;
  today_bookings: number;
  active_sessions: number;
  recent_audit_events: AdminAuditEventSummary[];
}

export interface AdminPromoCode {
  id: string;
  code: string;
  discount_percent: number;
  max_uses: number | null;
  current_uses: number;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

export interface CreatePromoCodeInput {
  code: string;
  discount_percent: number;
  max_uses?: number | null;
  expires_at?: string | null;
}

export interface VenueGameRecord {
  id: string;
  title: string;
  category: string;
  description: string | null;
  platform: string | null;
  image_url: string | null;
  rawg_id: number | null;
  unit_ids: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateVenueGameInput {
  title: string;
  category: string;
  description?: string | null;
  platform?: string | null;
  image_url?: string | null;
  unit_ids: string[];
  is_active?: boolean;
}

export interface GameCatalogRecord {
  rawg_id: number;
  title: string;
  slug: string | null;
  category: string;
  description: string | null;
  platform: string | null;
  image_url: string | null;
  released: string | null;
  rating: number | null;
  rawg_genres: string[];
  rawg_platforms: string[];
  cached_at: string;
}

export interface ImportVenueGameInput {
  rawg_id: number;
  unit_ids: string[];
  is_active?: boolean;
}

export interface FoodItemRecord {
  id: string;
  name: string;
  category: string;
  description: string | null;
  price: number;
  image_url: string | null;
  is_available: boolean;
  created_at: string;
  updated_at: string;
  /** Stock fields come from the provider menu endpoint only. */
  track_stock?: boolean;
  stock_quantity?: number;
  low_stock_threshold?: number | null;
  sold_out?: boolean;
  low_stock?: boolean;
}

export interface CreateFoodItemInput {
  name: string;
  category: string;
  description?: string | null;
  price: number;
  image_url?: string | null;
  is_available?: boolean;
  track_stock?: boolean;
  /** Opening stock, create only. Later changes go through adjustFoodStock. */
  stock_quantity?: number;
  low_stock_threshold?: number | null;
}

export interface UpdateFoodItemInput extends Partial<Omit<CreateFoodItemInput, "stock_quantity">> {
  clear_low_stock_threshold?: boolean;
}

export type FoodStockAdjustmentKind = "restock" | "count" | "waste";

export interface FoodStockMovementRecord {
  id: string;
  food_item_id: string;
  item_name: string;
  change: number;
  balance_after: number;
  reason: FoodStockAdjustmentKind | "order" | "order_cancelled";
  note: string | null;
  food_order_id: string | null;
  actor_name: string | null;
  created_at: string;
}

export type ProviderFoodOrderStatus =
  | "pending"
  | "preparing"
  | "ready"
  | "delivered"
  | "cancelled";

export interface ProviderFoodOrderItem {
  id: string;
  food_item_id: string;
  item_name: string;
  quantity: number;
  unit_price: number;
}

export interface ProviderFoodOrder {
  id: string;
  /** Null for counter sales, which are paid at the counter instead of added to a table's bill. */
  session_id: string | null;
  venue_id: string;
  status: ProviderFoodOrderStatus;
  notes: string | null;
  total_amount: number;
  items: ProviderFoodOrderItem[];
  created_at: string;
  updated_at: string;
  unit_name?: string | null;
  guest_name?: string | null;
  placed_by_customer?: boolean;
  preparing_at?: string | null;
  ready_at?: string | null;
  delivered_at?: string | null;
  cancelled_at?: string | null;
  cancel_reason?: string | null;
  counter?: boolean;
}

export interface CreateCounterSaleInput {
  venue_id: string;
  items: Array<{ food_item_id: string; quantity: number }>;
  guest_name?: string | null;
  notes?: string | null;
  payment_method: PaymentMethod;
  cash_received_amount?: number | null;
}

export interface ProviderFoodOrderEventPayload {
  venue_id: string;
  session_id: string;
  order_id: string;
  status: ProviderFoodOrderStatus;
}

export type NotificationCategory =
  | "booking"
  | "session"
  | "food_order"
  | "approval"
  | "account";

export type NotificationEventType =
  | "booking.requested.v1" | "booking.confirmed.v1" | "booking.rejected.v1"
  | "booking.cancelled.v1" | "booking.expired.v1" | "session.started.v1"
  | "session.extended.v1" | "session.extension_requested.v1" | "session.stopped.v1" | "food_order.created.v1"
  | "food_order.preparing.v1" | "food_order.ready.v1" | "food_order.delivered.v1"
  | "food_order.cancelled.v1" | "provider.approved.v1" | "provider.rejected.v1"
  | "provider.suspended.v1" | "venue.approved.v1" | "venue.rejected.v1"
  | "venue.removed.v1" | "venue.removal_declined.v1" | "account.password_changed.v1"
  | "account.email_changed.v1";

export type NotificationTargetKind =
  | "booking"
  | "session"
  | "food-order"
  | "venue"
  | "pending-confirmations"
  | "inbox";

export interface NotificationTargetDto {
  v: 1;
  audience: "provider" | "customer";
  kind: NotificationTargetKind;
  providerId?: string;
  venueId?: string;
  bookingId?: string;
  sessionId?: string;
  foodOrderId?: string;
}

export interface NotificationInboxItemDto {
  id: string;
  audience: "provider" | "customer";
  category: NotificationCategory;
  event_type: NotificationEventType;
  target: NotificationTargetDto;
  template_key: string;
  template_params: Record<string, string | number | boolean | null>;
  created_at: string;
  read_at: string | null;
  expires_at: string;
}

export interface NotificationInboxResponseDto {
  notifications: NotificationInboxItemDto[];
  next_cursor: string | null;
  unread_count: number;
}

export interface NotificationPreferencesResponseDto {
  preferences: Record<NotificationCategory, boolean>;
}

export interface CreateProviderFoodOrderInput {
  session_id: string;
  items: Array<{
    food_item_id: string;
    quantity: number;
  }>;
  notes?: string | null;
}

export interface ProviderPolicyRecord {
  vat_enabled: boolean;
  vat_percent: number;
  no_show_penalty_percent: number;
  cancellation_window_minutes: number;
  min_booking_duration_minutes: number;
  max_booking_duration_minutes: number;
  max_session_duration_minutes: number;
  auto_confirm_threshold_percent: number;
  max_active_sessions: number | null;
}

export type BookingDepositMode = "none" | "percent" | "fixed";
export type ReceiptLanguage = "en" | "ar";
export type ReceiptPaperWidth = "58mm" | "80mm";
export type ChargeScope = "all" | "food";

export interface ProviderBusinessProfileRecord {
  legal_name: string;
  trading_name: string | null;
  tax_registration_number: string | null;
  commercial_registration_number: string | null;
  billing_address: string | null;
  phone: string | null;
  email: string | null;
  logo_url: string | null;
  default_currency: string;
  default_timezone: string;
}

export interface VenueSettingsRecord {
  venue_id: string;
  receipt_prefix: string;
  receipt_footer: string | null;
  receipt_language_default: ReceiptLanguage;
  vat_enabled: boolean;
  vat_percent: number;
  vat_scope: ChargeScope;
  service_fee_enabled: boolean;
  service_fee_percent: number;
  service_fee_scope: ChargeScope;
  allow_partial_payments: boolean;
  require_payment_before_session_end: boolean;
  allow_unpaid_checkout: boolean;
  allowed_payment_methods: PaymentMethod[];
  booking_deposit_mode: BookingDepositMode;
  booking_deposit_percent: number;
  booking_deposit_fixed_amount: number;
  receipt_paper_width?: ReceiptPaperWidth;
}

export interface PaymentTransactionRecord {
  id: string;
  provider_id: string;
  venue_id: string;
  booking_id: string | null;
  session_id: string | null;
  receipt_id: string | null;
  cash_drawer_id: string | null;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  amount: number;
  currency: string;
  method: string;
  status: string;
  external_reference: string | null;
  collected_by_user_id: string | null;
  collected_at: string;
  note: string | null;
  metadata_json: Record<string, unknown> | null;
}

export interface ReceiptLineRecord {
  id: string;
  line_type: string;
  description: string;
  quantity: number;
  unit_price: number;
  subtotal: number;
  tax_amount: number;
  total: number;
  source_id: string | null;
  sort_order: number;
}

export interface ReceiptEventRecord {
  id: string;
  receipt_id: string;
  event_type: string;
  actor_user_id: string | null;
  metadata_json: Record<string, unknown> | null;
  created_at: string;
}

export interface CashDrawerRecord {
  id: string;
  provider_id: string;
  venue_id: string;
  opened_by_user_id: string | null;
  closed_by_user_id: string | null;
  opening_cash_amount: number;
  expected_cash_amount: number;
  counted_cash_amount: number | null;
  variance_amount: number | null;
  status: "open" | "closed";
  opened_at: string;
  closed_at: string | null;
  opening_note: string | null;
  closing_note: string | null;
}

export interface CashDrawerSummaryRecord {
  venue_id: string;
  currency: string;
  open_drawer: CashDrawerRecord | null;
  last_closed_drawer: CashDrawerRecord | null;
  payment_totals: Record<string, number>;
  transaction_count: number;
  expected_cash_amount: number;
}

export interface OpenCashDrawerInput {
  venue_id: string;
  opening_cash_amount: number;
  note?: string | null;
}

export interface CloseCashDrawerInput {
  counted_cash_amount: number;
  note?: string | null;
}

export interface DailyCloseoutReportRecord {
  venue_id: string;
  venue_name: string;
  date_local: string;
  currency: string;
  receipt_count: number;
  issued_total_amount: number;
  paid_total_amount: number;
  unpaid_total_amount: number;
  payment_totals: Record<string, number>;
  transaction_count: number;
  cash_drawer: CashDrawerRecord | null;
  expected_cash_amount: number;
  counted_cash_amount: number | null;
  variance_amount: number | null;
  generated_at: string;
}

export interface ReceiptRecord {
  id: string;
  provider_id: string;
  venue_id: string;
  receipt_number: string;
  sequence_number: number;
  status: string;
  source_type: string;
  source_id: string | null;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  cashier_user_id: string | null;
  subtotal_amount: number;
  discount_amount: number;
  service_fee_amount: number;
  tax_amount: number;
  total_amount: number;
  paid_amount: number;
  balance_due: number;
  currency: string;
  issued_at: string;
  voided_at: string | null;
  void_reason: string | null;
  pdf_url: string | null;
  print_count: number;
  metadata_json: Record<string, unknown> | null;
  lines: ReceiptLineRecord[];
  payments: PaymentTransactionRecord[];
  events: ReceiptEventRecord[];
}

export interface ReceiptListResponse {
  receipts: ReceiptRecord[];
}

export interface ReceiptPreviewLineRecord {
  line_type: string;
  description: string;
  quantity: number;
  unit_price: number;
  subtotal: number;
  tax_amount: number;
  total: number;
  source_id: string | null;
  sort_order: number;
}

export interface SessionReceiptPreviewRecord {
  provider_id: string;
  venue_id: string;
  session_id: string;
  booking_id: string | null;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  currency: string;
  subtotal_amount: number;
  discount_amount: number;
  service_fee_amount: number;
  tax_amount: number;
  total_amount: number;
  paid_amount: number;
  balance_due: number;
  allowed_payment_methods: PaymentMethod[];
  can_issue_unpaid: boolean;
  can_take_partial_payment: boolean;
  receipt_prefix: string;
  lines: ReceiptPreviewLineRecord[];
}

export interface IssueSessionReceiptInput {
  payment_method?: PaymentMethod | null;
  paid_amount?: number | null;
  cash_received_amount?: number | null;
  external_reference?: string | null;
  note?: string | null;
  mark_session_paid?: boolean;
}

export interface RecordReceiptPrintInput {
  copy_type?: "customer" | "merchant" | "internal";
}

export interface RecordReceiptPaymentInput {
  method: PaymentMethod;
  amount: number;
  cash_received_amount?: number | null;
  external_reference?: string | null;
  note?: string | null;
}

export interface RecordReceiptRefundInput {
  method: PaymentMethod;
  amount: number;
  reason: string;
  note?: string | null;
}

export interface VoidReceiptInput {
  reason: string;
}

export const APP_NAME = "gaamed";

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;
  readonly code: string | null;
  readonly data: ProviderOwnerSetupEmailResult | null;

  constructor(status: number, detail: string, code: string | null = null, data: ProviderOwnerSetupEmailResult | null = null) {
    super(detail);
    this.status = status;
    this.detail = detail;
    this.code = code;
    this.data = data;
  }
}

export function isOfflineApiError(error: unknown) {
  return error instanceof ApiError && error.status === 0;
}

function normalizeApiErrorDetail(detail: unknown) {
  if (typeof detail === "string" && detail.trim().length > 0) {
    return detail;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }

        if (item && typeof item === "object" && "msg" in item) {
          const message = item.msg;
          return typeof message === "string" ? message : null;
        }

        return null;
      })
      .filter((message): message is string => Boolean(message && message.trim()));

    if (messages.length > 0) {
      return messages.join(" ");
    }
  }

  if (detail && typeof detail === "object" && "msg" in detail) {
    const message = detail.msg;
    if (typeof message === "string" && message.trim().length > 0) {
      return message;
    }
  }

  return "Request failed";
}

export function canAccessAdmin(role: UserRole) {
  return role === "admin" || role === "support";
}

export function canAccessProvider(role: UserRole) {
  return role === "provider_owner" || role === "provider_staff";
}

export function getHomePath(role: UserRole) {
  if (canAccessAdmin(role)) {
    return "/admin";
  }

  if (canAccessProvider(role)) {
    return "/provider";
  }

  return "/access-denied";
}

export function getRoleLabel(role: UserRole | undefined) {
  switch (role) {
    case "admin":
      return "Admin";
    case "support":
      return "Support";
    case "provider_owner":
      return "Provider owner";
    case "provider_staff":
      return "Provider staff";
    case "customer":
      return "Customer";
    default:
      return "Unknown role";
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  disableOfflineFallback?: boolean;
  skipOfflineCacheWrite?: boolean;
}

export interface ProviderReadOptions {
  /** Bypass identity-scoped offline cache when freshness is an authority check. */
  networkOnly?: boolean;
  /** Let the caller reconcile a workspace snapshot before caching it. */
  skipOfflineCacheWrite?: boolean;
}

function shouldCacheOfflineResponse(path: string, method: string) {
  if (method !== "GET") {
    return false;
  }

  return (
    path.startsWith("/api/v1/provider")
  );
}

function getOfflineFallbackMessage() {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return "You're offline and this data is not cached yet.";
  }

  return "Unable to reach the server.";
}

// ---------------------------------------------------------------------------
// Provider role elevation
// ---------------------------------------------------------------------------

export const PROVIDER_ELEVATION_HEADER = "X-Provider-Elevation";
const PROVIDER_ELEVATION_PATH = "/api/v1/provider/elevation";
// Stop reusing a grant slightly before its server-side expiry so it is never
// attached to a request that outlives it.
const ELEVATION_EXPIRY_SKEW_MS = 15_000;

interface CachedElevationGrant {
  grantId: string;
  expiresAtMs: number;
}

let cachedElevationGrant: CachedElevationGrant | null = null;

export function getActiveElevationGrant() {
  if (
    cachedElevationGrant !== null &&
    cachedElevationGrant.expiresAtMs - ELEVATION_EXPIRY_SKEW_MS > Date.now()
  ) {
    return cachedElevationGrant;
  }

  return null;
}

export function cacheElevationGrant(grant: ProviderElevationResponse) {
  const expiresAtMs = Date.parse(grant.expires_at);
  cachedElevationGrant = Number.isFinite(expiresAtMs)
    ? { grantId: grant.grant_id, expiresAtMs }
    : null;
}

export function clearElevationGrant() {
  cachedElevationGrant = null;
}

export interface RoleElevationRequest {
  roleRequired: ProviderMembershipRole;
  detail: string;
}

/** Opens the manager-approval prompt. Resolves with the issued grant after
 *  approval, or null when the prompt is dismissed. Registered once from the
 *  provider app shell via setRoleElevationHandler. */
export type RoleElevationHandler = (
  request: RoleElevationRequest,
) => Promise<ProviderElevationResponse | null>;

let roleElevationHandler: RoleElevationHandler | null = null;

export function setRoleElevationHandler(handler: RoleElevationHandler | null) {
  roleElevationHandler = handler;
}

function isProviderMembershipRole(
  value: string | null,
): value is ProviderMembershipRole {
  return value === "owner" || value === "manager" || value === "front_desk";
}

async function readElevationErrorDetail(response: Response) {
  const rawBody = await response.text();
  if (rawBody.length === 0) {
    return "Request failed";
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    return rawBody.trim().length > 0 ? rawBody : "Request failed";
  }

  try {
    const payload = JSON.parse(rawBody) as { detail?: unknown };
    return normalizeApiErrorDetail(payload.detail);
  } catch {
    return "Request failed";
  }
}

async function ensureProviderElevationGrant(request: RoleElevationRequest) {
  const activeGrant = getActiveElevationGrant();
  if (activeGrant) {
    return activeGrant;
  }

  if (!roleElevationHandler) {
    return null;
  }

  const granted = await roleElevationHandler(request);
  if (!granted) {
    return null;
  }

  cacheElevationGrant(granted);
  return getActiveElevationGrant();
}

export async function performApiRequest<T>(path: string, options: RequestOptions = {}) {
  const assistance = getAssistance();
  const requestScope = getOfflineIdentityScope();
  const requestRevision = getOfflineSessionRevision().revision;
  const isPrivateScopedRequest = path.startsWith("/api/v1/provider");
  if (isPrivateScopedRequest && !isOfflineSessionCurrent()) {
    throw new ApiError(401, "Authentication changed in another tab.");
  }
  const headers = new Headers(options.headers);
  const method = (options.method ?? "GET").toUpperCase();
  const shouldUseOfflineCache =
    !assistance && !options.disableOfflineFallback && shouldCacheOfflineResponse(path, method);

  if (
    shouldUseOfflineCache &&
    typeof navigator !== "undefined" &&
    !navigator.onLine
  ) {
    if (!isOfflineSessionCurrent()) throw new ApiError(401, "Authentication changed in another tab.");
    const cachedPayload = readCachedApiResponse<T>(path);
    if (cachedPayload !== null) {
      return cachedPayload;
    }
  }

  if (options.body !== undefined) {
    headers.set("content-type", "application/json");
  }

  headers.set("accept", "application/json");
  if (assistance) headers.set("X-Admin-Assistance", assistance.token);

  // Role-restricted endpoints are all mutations; a still-valid elevation grant
  // is attached up front so follow-up restricted calls skip the 403 round trip.
  const isElevationCapableRequest =
    path.startsWith("/api/v1/provider/") && path !== PROVIDER_ELEVATION_PATH;
  if (
    isElevationCapableRequest &&
    method !== "GET" &&
    !headers.has(PROVIDER_ELEVATION_HEADER)
  ) {
    const activeGrant = getActiveElevationGrant();
    if (activeGrant) {
      headers.set(PROVIDER_ELEVATION_HEADER, activeGrant.grantId);
    }
  }

  const executeFetch = async () => {
    const controller = new AbortController();
    const callerSignal = options.signal;
    const abortFromCaller = () => controller.abort();
    if (callerSignal) {
      if (callerSignal.aborted) controller.abort();
      else callerSignal.addEventListener("abort", abortFromCaller, { once: true });
    }
    inFlightControllers.add(controller);

    const {
      body,
      disableOfflineFallback: _disableOfflineFallback,
      skipOfflineCacheWrite: _skipOfflineCacheWrite,
      ...requestInit
    } = options;

    try {
      return await fetch(path, {
        ...requestInit,
        signal: controller.signal,
        body: body === undefined ? undefined : JSON.stringify(body),
        credentials: "include",
        headers,
      });
    } finally {
      inFlightControllers.delete(controller);
      callerSignal?.removeEventListener("abort", abortFromCaller);
    }
  };

  let response: Response;
  try {
    response = await executeFetch();
  } catch {
    if (shouldUseOfflineCache && isOfflineSessionCurrent()) {
      const cachedPayload = readCachedApiResponse<T>(path);
      if (cachedPayload !== null) {
        return cachedPayload;
      }
    }

    throw new ApiError(0, getOfflineFallbackMessage());
  }

  // Role-restricted provider endpoints answer 403 with an x-role-required
  // header. Prompt once for manager/owner approval, then replay the original
  // request once with the elevation grant attached. Cancelling the prompt
  // surfaces the original 403 detail.
  if (response.status === 403 && isElevationCapableRequest) {
    const roleRequired = response.headers.get("x-role-required");
    if (isProviderMembershipRole(roleRequired)) {
      const elevationDetail = await readElevationErrorDetail(response);
      const grant = await ensureProviderElevationGrant({
        roleRequired,
        detail: elevationDetail,
      });
      if (grant) {
        headers.set(PROVIDER_ELEVATION_HEADER, grant.grantId);
        try {
          response = await executeFetch();
        } catch {
          throw new ApiError(0, getOfflineFallbackMessage());
        }
      } else {
        throw new ApiError(response.status, elevationDetail);
      }
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const rawBody = await response.text();
  const contentType = response.headers.get("content-type") ?? "";
  const payload =
    rawBody.length === 0
      ? undefined
      : contentType.includes("application/json")
        ? (JSON.parse(rawBody) as { detail?: string } | T)
        : rawBody;

  if (!response.ok) {
    // Gateway/proxy failures (deploys, origin restarts) come back as plain
    // text or HTML from Cloudflare — showing that raw reads like a crash.
    const isGatewayFailure =
      response.status === 502 ||
      response.status === 503 ||
      response.status === 504 ||
      (response.status >= 520 && response.status <= 530);
    const rawDetail = typeof payload === "object" && payload && "detail" in payload
      ? (payload as { detail?: unknown }).detail
      : undefined;
    const errorCode =
      typeof rawDetail === "object" && rawDetail && "code" in rawDetail &&
      typeof rawDetail.code === "string"
        ? rawDetail.code
        : null;
    const isSetupEmailDeliveryFailure = response.status === 503 && errorCode === "setup_email_delivery_failed";
    const detail = isGatewayFailure && !isSetupEmailDeliveryFailure && rawDetail === undefined
      ? "The service is temporarily unavailable. Please try again in a moment."
      : typeof payload === "object" && payload && "detail" in payload
        ? normalizeApiErrorDetail(payload.detail)
        : typeof payload === "string" && payload.trim().length > 0
          ? payload
          : "Request failed";
    // The elevation endpoint answers 401 for bad approver credentials — that
    // is not a session failure, so the offline identity must survive it.
    const errorData = typeof rawDetail === "object" && rawDetail && "status" in rawDetail &&
      "retryable" in rawDetail && "access_locked" in rawDetail && "sessions_revoked" in rawDetail
      ? rawDetail as ProviderOwnerSetupEmailResult
      : null;
    if (response.status === 401 && path !== PROVIDER_ELEVATION_PATH) invalidateOfflineState();
    throw new ApiError(response.status, detail, errorCode, errorData);
  }

  if (shouldUseOfflineCache && !options.skipOfflineCacheWrite && payload !== undefined) {
    // A response from a request started before logout/account switching must
    // never repopulate the newly active identity's cache.
    if (
      requestScope === getOfflineIdentityScope() &&
      requestRevision === getOfflineSessionRevision().revision &&
      isOfflineSessionCurrent()
    ) {
      writeCachedApiResponseForScope(path, payload as T, requestScope);
    }
  }

  return payload as T;
}

async function apiRequest<T>(path: string, options: RequestOptions = {}) {
  return performApiRequest<T>(path, options);
}

/** Network-only request used by the platform console client (lib/admin-console-api.ts). */
export function adminConsoleRequest<T>(path: string, options: Omit<RequestOptions, "disableOfflineFallback"> = {}) {
  return apiRequest<T>(path, { ...options, disableOfflineFallback: true });
}

const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  "booking", "session", "food_order", "approval", "account",
];
const NOTIFICATION_EVENT_TYPES: NotificationEventType[] = [
  "booking.requested.v1", "booking.confirmed.v1", "booking.rejected.v1", "booking.cancelled.v1", "booking.expired.v1",
  "session.started.v1", "session.extended.v1", "session.extension_requested.v1", "session.stopped.v1", "food_order.created.v1", "food_order.preparing.v1",
  "food_order.ready.v1", "food_order.delivered.v1", "food_order.cancelled.v1", "provider.approved.v1", "provider.rejected.v1",
  "provider.suspended.v1", "venue.approved.v1", "venue.rejected.v1", "venue.removed.v1", "venue.removal_declined.v1",
  "account.password_changed.v1", "account.email_changed.v1",
];
const NOTIFICATION_TARGET_KINDS: NotificationTargetKind[] = [
  "booking", "session", "food-order", "venue", "pending-confirmations", "inbox",
];
const CANONICAL_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const CANONICAL_UTC_Z = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,6})?Z$/;
const TEMPLATE_PARAMETER_KEY = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;
const NOTIFICATION_CURSOR = /^[A-Za-z0-9_-]{132}\.[0-9a-f]{64}$/;
// The server emits a 99-byte canonical payload: 132 base64url chars, one dot, and a 64-character HMAC.
export const NOTIFICATION_CURSOR_MAX_LENGTH = 197;

function notificationRecord(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`Invalid notification ${label}.`);
  }
  return value as Record<string, unknown>;
}

function notificationString(value: unknown, label: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`Invalid notification ${label}.`);
  }
  return value;
}

function notificationUuid(value: unknown, label: string): string {
  const text = notificationString(value, label);
  if (!CANONICAL_UUID.test(text)) throw new Error(`Invalid notification ${label}.`);
  return text;
}

function notificationDate(value: unknown, label: string): string {
  const text = notificationString(value, label);
  const match = CANONICAL_UTC_Z.exec(text);
  if (!match) throw new Error(`Invalid notification ${label}.`);
  const parts = match.slice(1, 7).map(Number);
  const [year, month, day, hour, minute, second] = parts;
  const parsed = new Date(0);
  parsed.setUTCFullYear(year!, month! - 1, day!);
  parsed.setUTCHours(hour!, minute!, second!, 0);
  if (
    !Number.isFinite(parsed.getTime()) || parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month! - 1 ||
    parsed.getUTCDate() !== day || parsed.getUTCHours() !== hour || parsed.getUTCMinutes() !== minute || parsed.getUTCSeconds() !== second
  ) throw new Error(`Invalid notification ${label}.`);
  return text;
}

function notificationCursor(value: unknown, label: string): string {
  const text = notificationString(value, label);
  if (text.length > NOTIFICATION_CURSOR_MAX_LENGTH || !NOTIFICATION_CURSOR.test(text)) {
    throw new Error(`Invalid notification ${label}.`);
  }
  return text;
}

function notificationExactKeys(record: Record<string, unknown>, keys: string[], label: string) {
  if (Object.keys(record).length !== keys.length || keys.some((key) => !(key in record))) {
    throw new Error(`Invalid notification ${label}.`);
  }
}

export function parseNotificationTargetDto(value: unknown): NotificationTargetDto {
  const record = notificationRecord(value, "target");
  const audience = record.audience;
  const kind = record.kind;
  if ((audience !== "provider" && audience !== "customer") ||
      !NOTIFICATION_TARGET_KINDS.includes(kind as NotificationTargetKind) || record.v !== 1) {
    throw new Error("Invalid notification target.");
  }
  const targetKind = kind as NotificationTargetKind;
  if (audience === "customer" && targetKind === "pending-confirmations") {
    throw new Error("Invalid notification target.");
  }
  const required: Record<NotificationTargetKind, string[]> = {
    booking: ["bookingId"],
    session: ["sessionId"],
    "food-order": ["foodOrderId"],
    venue: ["venueId"],
    "pending-confirmations": [],
    inbox: [],
  };
  const shared = audience === "provider"
    ? targetKind === "inbox" ? ["providerId"] : ["providerId", "venueId"]
    : [];
  const keys = ["v", "audience", "kind", ...shared, ...required[targetKind]];
  notificationExactKeys(record, keys, "target fields");
  const result: NotificationTargetDto = { v: 1, audience, kind: targetKind };
  for (const key of shared.concat(required[targetKind])) {
    const text = notificationUuid(record[key], `target.${key}`);
    if (key === "providerId") result.providerId = text;
    if (key === "venueId") result.venueId = text;
    if (key === "bookingId") result.bookingId = text;
    if (key === "sessionId") result.sessionId = text;
    if (key === "foodOrderId") result.foodOrderId = text;
  }
  return result;
}

function categoryForNotificationEvent(eventType: NotificationEventType): NotificationCategory {
  if (eventType.startsWith("booking.")) return "booking";
  if (eventType.startsWith("session.")) return "session";
  if (eventType.startsWith("food_order.")) return "food_order";
  if (eventType.startsWith("provider.") || eventType.startsWith("venue.")) return "approval";
  return "account";
}

function isCompatibleNotificationTarget(eventType: NotificationEventType, audience: "provider" | "customer", kind: NotificationTargetKind) {
  if (eventType.startsWith("booking.")) {
    if (eventType === "booking.requested.v1") return audience === "customer" ? kind === "booking" : kind === "booking" || kind === "pending-confirmations";
    if (eventType === "booking.cancelled.v1") return kind === "booking";
    return audience === "customer" && kind === "booking";
  }
  if (eventType === "session.extension_requested.v1") return audience === "provider" && kind === "session";
  if (eventType.startsWith("session.")) return audience === "customer" && kind === "session";
  if (eventType.startsWith("food_order.")) {
    return audience === "customer"
      ? kind === "food-order"
      : (eventType === "food_order.created.v1" || eventType === "food_order.cancelled.v1") && kind === "food-order";
  }
  if (eventType.startsWith("provider.")) return audience === "provider" && kind === "inbox";
  if (eventType.startsWith("venue.")) return audience === "provider" && kind === "venue";
  return kind === "inbox";
}

export function parseNotificationInboxResponse(value: unknown): NotificationInboxResponseDto {
  const record = notificationRecord(value, "inbox");
  notificationExactKeys(record, ["notifications", "next_cursor", "unread_count"], "inbox fields");
  if (!Array.isArray(record.notifications) ||
      (record.next_cursor !== null && (typeof record.next_cursor !== "string" || record.next_cursor.length === 0)) ||
      typeof record.unread_count !== "number" || !Number.isSafeInteger(record.unread_count) || record.unread_count < 0) {
    throw new Error("Invalid notification inbox.");
  }
  return {
    notifications: record.notifications.map((item) => {
      const row = notificationRecord(item, "item");
      notificationExactKeys(row, ["id", "audience", "category", "event_type", "target", "template_key", "template_params", "created_at", "read_at", "expires_at"], "item fields");
      if ((row.audience !== "provider" && row.audience !== "customer") ||
          !NOTIFICATION_CATEGORIES.includes(row.category as NotificationCategory) ||
          !NOTIFICATION_EVENT_TYPES.includes(row.event_type as NotificationEventType) ||
          (row.read_at !== null && typeof row.read_at !== "string")) {
        throw new Error("Invalid notification item.");
      }
      const params = notificationRecord(row.template_params, "template parameters");
      if (Object.keys(params).length > 32) throw new Error("Invalid notification template parameters.");
      for (const [key, param] of Object.entries(params)) {
        if (!TEMPLATE_PARAMETER_KEY.test(key) || (typeof param === "string" && param.length > 500) ||
            (typeof param === "number" && !Number.isFinite(param)) ||
            (param !== null && !["string", "number", "boolean"].includes(typeof param))) {
          throw new Error("Invalid notification template parameters.");
        }
      }
      const eventType = row.event_type as NotificationEventType;
      const target = parseNotificationTargetDto(row.target);
      if (
        target.audience !== row.audience || row.category !== categoryForNotificationEvent(eventType) ||
        !isCompatibleNotificationTarget(eventType, row.audience, target.kind) ||
        row.template_key !== `${eventType.slice(0, -3)}.${row.audience}.v1`
      ) throw new Error("Invalid notification item.");
      return {
        id: notificationUuid(row.id, "id"),
        audience: row.audience,
        category: row.category as NotificationCategory,
        event_type: eventType,
        target,
        template_key: notificationString(row.template_key, "template key"),
        template_params: params as NotificationInboxItemDto["template_params"],
        created_at: notificationDate(row.created_at, "created at"),
        read_at: row.read_at === null ? null : notificationDate(row.read_at, "read at"),
        expires_at: notificationDate(row.expires_at, "expires at"),
      };
    }),
    next_cursor: record.next_cursor === null ? null : notificationCursor(record.next_cursor, "cursor"),
    unread_count: record.unread_count,
  };
}

export function parseNotificationPreferencesResponse(value: unknown): NotificationPreferencesResponseDto {
  const record = notificationRecord(value, "preferences response");
  notificationExactKeys(record, ["preferences"], "preferences response fields");
  const preferences = notificationRecord(record.preferences, "preferences");
  notificationExactKeys(preferences, NOTIFICATION_CATEGORIES, "preferences fields");
  for (const category of NOTIFICATION_CATEGORIES) {
    if (typeof preferences[category] !== "boolean") throw new Error("Invalid notification preferences.");
  }
  return { preferences: preferences as NotificationPreferencesResponseDto["preferences"] };
}

export const notificationApi = {
  async listInbox(cursor?: string | null) {
    const query = cursor == null ? "" : `?cursor=${encodeURIComponent(notificationCursor(cursor, "cursor"))}`;
    return parseNotificationInboxResponse(await apiRequest<unknown>(`/api/v1/notifications${query}`, {
      disableOfflineFallback: true,
    }));
  },
  async markRead(notificationId: string) {
    const response = notificationRecord(await apiRequest<unknown>(`/api/v1/notifications/${encodeURIComponent(notificationId)}/read`, {
      method: "POST", disableOfflineFallback: true,
    }), "read response");
    if (response.success !== true) throw new Error("Invalid notification read response.");
  },
  async markAllRead() {
    const response = notificationRecord(await apiRequest<unknown>("/api/v1/notifications/read-all", {
      method: "POST", disableOfflineFallback: true,
    }), "read-all response");
    if (response.success !== true || typeof response.updated_count !== "number" || !Number.isSafeInteger(response.updated_count) || response.updated_count < 0) {
      throw new Error("Invalid notification read-all response.");
    }
    return response.updated_count;
  },
  async getPreferences() {
    return parseNotificationPreferencesResponse(await apiRequest<unknown>("/api/v1/notification-preferences", {
      disableOfflineFallback: true,
    }));
  },
  async updatePreferences(preferences: Partial<Record<NotificationCategory, boolean>>) {
    const entries = Object.entries(preferences);
    if (entries.length === 0 || entries.some(([category, enabled]) => !NOTIFICATION_CATEGORIES.includes(category as NotificationCategory) || typeof enabled !== "boolean")) {
      throw new Error("Invalid notification preference update.");
    }
    return parseNotificationPreferencesResponse(await apiRequest<unknown>("/api/v1/notification-preferences", {
      method: "PUT", body: { preferences }, disableOfflineFallback: true,
    }));
  },
};

export const authApi = {
  me() {
    return apiRequest<SessionResponse>("/api/v1/auth/me");
  },
  status() {
    return apiRequest<AuthStatusResponse>("/api/v1/auth/status");
  },
  /** POST /auth/refresh — rotates the session via the HttpOnly refresh
   *  cookie. Throws ApiError(401) when the session is unrecoverable. */
  refresh() {
    return apiRequest<AuthStatusResponse>("/api/v1/auth/refresh", {
      method: "POST",
    });
  },
  login(credentials: { email: string; password: string }) {
    return apiRequest<SessionResponse>("/api/v1/auth/login", {
      method: "POST",
      body: credentials,
    });
  },
  forgotPassword(email: string) {
    return apiRequest<void>("/api/v1/auth/forgot-password", {
      method: "POST",
      body: { email },
    });
  },
  resetPassword(token: string, newPassword: string) {
    return apiRequest<void>("/api/v1/auth/reset-password", {
      method: "POST",
      body: { token, new_password: newPassword },
    });
  },
  setPassword(token: string, newPassword: string) {
    return apiRequest<void>("/api/v1/auth/set-password", {
      method: "POST",
      body: { token, new_password: newPassword },
    });
  },
  verifyEmail(payload: { email: string; code: string; booking_draft_id?: string | null }) {
    return apiRequest<SessionResponse>("/api/v1/auth/verify-email", {
      method: "POST",
      body: payload,
    });
  },
  resendVerification(email: string) {
    return apiRequest<void>("/api/v1/auth/resend-verification", {
      method: "POST",
      body: { email },
    });
  },
  logout() {
    return apiRequest<{ ok: boolean }>("/api/v1/auth/logout", {
      method: "POST",
      keepalive: true,
    });
  },
  registerProvider(payload: ProviderRegistrationInput) {
    return apiRequest<SessionResponse | VerificationPendingResponse>("/api/v1/auth/provider-register", {
      method: "POST",
      body: payload,
    });
  },
  getProviderRegistration() {
    return apiRequest<ProviderRegistrationRecord>("/api/v1/auth/provider-register");
  },
  resubmitProviderRegistration(payload: ProviderRegistrationResubmissionInput) {
    return apiRequest<SessionResponse>("/api/v1/auth/provider-register", {
      method: "PUT",
      body: payload,
    });
  },
};

export const workspaceApi = {
  adminSummary() {
    return apiRequest<WorkspaceSummary>("/api/v1/admin/summary");
  },
  providerSummary() {
    return apiRequest<WorkspaceSummary>("/api/v1/provider/summary");
  },
};

export const adminApi = {
  listProviders() {
    return apiRequest<AdminProviderAccountsResponse>("/api/v1/admin/providers");
  },
  getProvider(providerId: string) {
    return apiRequest<AdminProviderDetail>(`/api/v1/admin/providers/${providerId}`);
  },
  createProvider(payload: CreateAdminProviderInput) {
    return apiRequest<CreateAdminProviderResponse>("/api/v1/admin/providers", {
      method: "POST",
      body: payload,
    });
  },
  resendProviderOwnerSetup(providerId: string) {
    return apiRequest<void>(`/api/v1/admin/providers/${providerId}/owner-setup/resend`, {
      method: "POST",
    });
  },
  updateProvider(providerId: string, payload: UpdateAdminProviderInput) {
    return apiRequest<AdminProviderAccount>(`/api/v1/admin/providers/${providerId}`, {
      method: "PATCH",
      body: payload,
    });
  },
  bulkProviderStatus(payload: BulkAdminProviderStatusInput) {
    return apiRequest<AdminProviderAccountsResponse>("/api/v1/admin/providers/bulk-status", {
      method: "POST",
      body: payload,
    });
  },
  bulkVenueStatus(payload: BulkAdminVenueStatusInput) {
    return apiRequest<AdminVenuesResponse>("/api/v1/admin/venues/bulk-status", {
      method: "POST",
      body: payload,
    });
  },
  listVenues() {
    return apiRequest<AdminVenuesResponse>("/api/v1/admin/venues");
  },
  updateProviderStatus(
    providerId: string,
    payload: UpdateAdminProviderStatusInput,
  ) {
    return apiRequest<AdminProviderAccount>(`/api/v1/admin/providers/${providerId}/status`, {
      method: "PUT",
      body: payload,
    });
  },
  updateVenueStatus(venueId: string, payload: UpdateAdminVenueStatusInput) {
    return apiRequest<AdminVenueReview>(`/api/v1/admin/venues/${venueId}/status`, {
      method: "PUT",
      body: payload,
    });
  },
  getAnalytics() {
    return apiRequest<AdminAnalytics>("/api/v1/admin/analytics");
  },
  getPolicies() {
    return apiRequest<{ policies: Record<string, string> }>("/api/v1/admin/policies");
  },
  updatePolicies(policies: Record<string, string>) {
    return apiRequest<{ policies: Record<string, string> }>("/api/v1/admin/policies", {
      method: "PUT",
      body: { policies },
    });
  },
  listPromoCodes() {
    return apiRequest<{ promo_codes: AdminPromoCode[] }>("/api/v1/admin/promo-codes");
  },
  createPromoCode(payload: CreatePromoCodeInput) {
    return apiRequest<AdminPromoCode>("/api/v1/admin/promo-codes", {
      method: "POST",
      body: payload,
    });
  },
  updatePromoCode(codeId: string, payload: Partial<CreatePromoCodeInput & { is_active: boolean }>) {
    return apiRequest<AdminPromoCode>(`/api/v1/admin/promo-codes/${codeId}`, {
      method: "PUT",
      body: payload,
    });
  },
  listPendingReviews() {
    return apiRequest<AdminPendingReviewsResponse>("/api/v1/admin/reviews/pending");
  },
  updateReviewStatus(reviewId: string, payload: UpdateAdminReviewStatusInput) {
    return apiRequest<AdminReviewRecord>(`/api/v1/admin/reviews/${reviewId}/status`, {
      method: "PUT",
      body: payload,
    });
  },
  listPendingReviewReports() {
    return apiRequest<AdminReviewReport[]>("/api/v1/admin/ugc/reports");
  },
  resolveReviewReport(reportId: string, payload: { decision: ReviewReportResolution; rationale: string }) {
    return apiRequest<AdminReviewReport>(`/api/v1/admin/ugc/reports/${reportId}`, { method: "PUT", body: payload });
  },
};

export const providerApi = {
  resolveVenueLocation(input: string) {
    return apiRequest<VenueLocationResolveResponse>("/api/v1/provider/venue-location/resolve", {
      method: "POST",
      body: { query: input },
      disableOfflineFallback: true,
    });
  },
  listBookings(filters?: ProviderBookingFilters) {
    const params = new URLSearchParams();

    if (filters?.venueId) {
      params.set("venue_id", filters.venueId);
    }

    if (filters?.unitId) {
      params.set("unit_id", filters.unitId);
    }

    if (filters?.statuses && filters.statuses.length > 0) {
      params.set("statuses", filters.statuses.join(","));
    }

    if (filters?.scheduledFromLocal) {
      params.set("scheduled_from_local", filters.scheduledFromLocal);
    }

    if (filters?.scheduledToLocal) {
      params.set("scheduled_to_local", filters.scheduledToLocal);
    }

    const queryString = params.toString();
    const query = queryString ? `?${queryString}` : "";
    return apiRequest<ProviderBookingsResponse>(`/api/v1/provider/bookings${query}`);
  },
  getBooking(bookingId: string, options?: ProviderReadOptions) {
    return apiRequest<ProviderBooking>(`/api/v1/provider/bookings/${bookingId}`, {
      disableOfflineFallback: options?.networkOnly,
    });
  },
  listCustomers(search?: string) {
    const query = search ? `?search=${encodeURIComponent(search)}` : "";
    return apiRequest<ProviderCustomersResponse>(`/api/v1/provider/customers${query}`);
  },
  getCustomerHistory(filters: ProviderCustomerHistoryFilters) {    const params = new URLSearchParams();

    if (filters.customerName) {
      params.set("customer_name", filters.customerName);
    }

    if (filters.customerUserId) params.set("customer_user_id", filters.customerUserId);

    if (filters.customerEmail) {
      params.set("customer_email", filters.customerEmail);
    }

    if (filters.customerPhone) {
      params.set("customer_phone", filters.customerPhone);
    }

    if (filters.excludeBookingId) {
      params.set("exclude_booking_id", filters.excludeBookingId);
    }

    if (filters.excludeSessionId) {
      params.set("exclude_session_id", filters.excludeSessionId);
    }

    const queryString = params.toString();
    const query = queryString ? `?${queryString}` : "";
    return apiRequest<ProviderCustomerHistoryResponse>(
      `/api/v1/provider/customers/history${query}`,
    );
  },
  getCustomerDetail(filters: ProviderCustomerDetailFilters) {
    const params = new URLSearchParams();

    if (filters.customerUserId) params.set("customer_user_id", filters.customerUserId);

    if (filters.customerEmail) {
      params.set("customer_email", filters.customerEmail);
    }

    if (filters.customerPhone) {
      params.set("customer_phone", filters.customerPhone);
    }

    if (filters.customerName) {
      params.set("customer_name", filters.customerName);
    }

    const queryString = params.toString();
    const query = queryString ? `?${queryString}` : "";
    return apiRequest<ProviderCustomerDetailResponse>(
      `/api/v1/provider/customers/detail${query}`,
    );
  },
  createBooking(payload: CreateProviderBookingInput) {
    return apiRequest<ProviderBooking>("/api/v1/provider/bookings", {
      method: "POST",
      body: payload,
    });
  },
  updateBooking(
    bookingId: string,
    payload: UpdateProviderBookingInput,
  ) {
    return apiRequest<ProviderBooking>(`/api/v1/provider/bookings/${bookingId}`, {
      method: "PUT",
      body: payload,
    });
  },
  updateBookingStatus(
    bookingId: string,
    payload: UpdateProviderBookingStatusInput,
  ) {
    return apiRequest<ProviderBooking>(`/api/v1/provider/bookings/${bookingId}/status`, {
      method: "PUT",
      body: payload,
    });
  },
  listSessions(filters?: ProviderSessionFilters, options?: ProviderReadOptions) {
    const params = new URLSearchParams();

    if (filters?.venueId) {
      params.set("venue_id", filters.venueId);
    }

    if (filters?.unitId) {
      params.set("unit_id", filters.unitId);
    }

    if (filters?.statuses && filters.statuses.length > 0) {
      params.set("statuses", filters.statuses.join(","));
    }

    const queryString = params.toString();
    const query = queryString ? `?${queryString}` : "";
    return apiRequest<ProviderSessionsResponse>(`/api/v1/provider/sessions${query}`, {
      disableOfflineFallback: options?.networkOnly,
    });
  },
  listNotes(filters?: ProviderNotesFilters) {
    const params = new URLSearchParams();

    if (filters?.venueId) {
      params.set("venue_id", filters.venueId);
    }

    const queryString = params.toString();
    const query = queryString ? `?${queryString}` : "";
    return apiRequest<ProviderNotesResponse>(`/api/v1/provider/notes${query}`);
  },
  startSessionFromBooking(
    bookingId: string,
    payload: StartSessionFromBookingInput = {},
  ) {
    return apiRequest<ProviderSession>(`/api/v1/provider/bookings/${bookingId}/session`, {
      method: "POST",
      body: payload,
    });
  },
  startWalkInSession(payload: StartWalkInSessionInput) {
    return apiRequest<ProviderSession>("/api/v1/provider/sessions/walk-in", {
      method: "POST",
      body: payload,
    });
  },
  extendSession(sessionId: string, payload: ExtendProviderSessionRequest) {
    return apiRequest<ProviderSession>(`/api/v1/provider/sessions/${sessionId}/extend`, {
      method: "PUT",
      body: payload,
    });
  },
  // Approval reads/writes never use offline snapshots or the mutation queue.
  // The backend list is membership-scoped (no venue query parameter); the UI
  // additionally filters its authoritative session.venue_id to the active venue.
  listExtensionRequests(signal?: AbortSignal) {
    return apiRequest<ProviderExtensionRequestsResponse>("/api/v1/provider/sessions/extension-requests", {
      disableOfflineFallback: true, cache: "no-store", signal,
    });
  },
  getExtensionRequest(quoteId: string, signal?: AbortSignal) {
    return apiRequest<ProviderExtensionRequest>(`/api/v1/provider/sessions/extension-requests/${encodeURIComponent(quoteId)}`, {
      disableOfflineFallback: true, cache: "no-store", signal,
    });
  },
  approveExtensionRequest(quoteId: string, signal?: AbortSignal) {
    return apiRequest<ProviderExtensionRequest>(`/api/v1/provider/sessions/extension-requests/${encodeURIComponent(quoteId)}/approve`, {
      method: "POST", disableOfflineFallback: true, cache: "no-store", signal,
    });
  },
  rejectExtensionRequest(quoteId: string, signal?: AbortSignal) {
    return apiRequest<ProviderExtensionRequest>(`/api/v1/provider/sessions/extension-requests/${encodeURIComponent(quoteId)}/reject`, {
      method: "POST", disableOfflineFallback: true, cache: "no-store", signal,
    });
  },
  stopSession(sessionId: string, payload: StopProviderSessionInput = {}) {
    return apiRequest<ProviderSession>(`/api/v1/provider/sessions/${sessionId}/stop`, {
      method: "PUT",
      body: payload,
    });
  },
  listVenues(options?: ProviderReadOptions) {
    return apiRequest<ProviderVenuesResponse>("/api/v1/provider/venues", {
      disableOfflineFallback: options?.networkOnly,
      skipOfflineCacheWrite: options?.skipOfflineCacheWrite,
    });
  },
  createVenue(payload: CreateVenueInput) {
    return apiRequest<ProviderVenue>("/api/v1/provider/venues", {
      method: "POST",
      body: payload,
    });
  },
  updateVenue(venueId: string, payload: CreateVenueInput) {
    return apiRequest<ProviderVenue>(`/api/v1/provider/venues/${venueId}`, {
      method: "PUT",
      body: payload,
    });
  },
  requestVenueRemoval(venueId: string, payload: RequestVenueRemovalInput) {
    return apiRequest<ProviderVenue>(`/api/v1/provider/venues/${venueId}/removal-request`, {
      method: "POST",
      body: payload,
    });
  },
  updateVenueAvailability(
    venueId: string,
    payload: UpdateVenueAvailabilityInput,
  ) {
    return apiRequest<ProviderVenue>(`/api/v1/provider/venues/${venueId}/availability`, {
      method: "PUT",
      body: payload,
    });
  },
  createVenueBlackoutWindow(
    venueId: string,
    payload: CreateVenueBlackoutWindowInput,
  ) {
    return apiRequest<ProviderVenue>(`/api/v1/provider/venues/${venueId}/blackouts`, {
      method: "POST",
      body: payload,
    });
  },
  deleteVenueBlackoutWindow(venueId: string, blackoutId: string) {
    return apiRequest<ProviderVenue>(
      `/api/v1/provider/venues/${venueId}/blackouts/${blackoutId}`,
      {
        method: "DELETE",
      },
    );
  },
  createUnit(venueId: string, payload: CreateUnitInput) {
    return apiRequest<ProviderUnit>(`/api/v1/provider/venues/${venueId}/units`, {
      method: "POST",
      body: payload,
    });
  },
  updateUnit(venueId: string, unitId: string, payload: CreateUnitInput) {
    return apiRequest<ProviderUnit>(
      `/api/v1/provider/venues/${venueId}/units/${unitId}`,
      {
        method: "PUT",
        body: payload,
      },
    );
  },
  deleteUnit(venueId: string, unitId: string) {
    return apiRequest<void>(`/api/v1/provider/venues/${venueId}/units/${unitId}`, {
      method: "DELETE",
    });
  },
  listTeam() {
    return apiRequest<ProviderTeamResponse>("/api/v1/provider/team");
  },
  createTeamMember(payload: CreateProviderStaffInput) {
    return apiRequest<ProviderTeamMember>("/api/v1/provider/team", {
      method: "POST",
      body: payload,
    });
  },
  updateTeamMemberRole(userId: string, payload: UpdateProviderStaffRoleInput) {
    return apiRequest<ProviderTeamMember>(`/api/v1/provider/team/${userId}/role`, {
      method: "PUT",
      body: payload,
    });
  },
  updateTeamMemberVenues(userId: string, payload: UpdateProviderStaffVenuesInput) {
    return apiRequest<ProviderTeamMember>(`/api/v1/provider/team/${userId}/venues`, {
      method: "PUT",
      body: payload,
    });
  },
  revokeTeamMember(userId: string) {
    return apiRequest<void>(`/api/v1/provider/team/${userId}`, {
      method: "DELETE",
    });
  },
  requestElevation(payload: RequestProviderElevationInput) {
    return apiRequest<ProviderElevationResponse>("/api/v1/provider/elevation", {
      method: "POST",
      body: payload,
    });
  },

  // Games menu
  searchGameCatalog(query: string) {
    const params = new URLSearchParams({ query });
    return apiRequest<{ results: GameCatalogRecord[]; source: "cache" | "rawg" | "local" }>(`/api/v1/provider/games/search?${params.toString()}`);
  },
  listGames(venueId: string) {
    return apiRequest<{ games: VenueGameRecord[] }>(`/api/v1/provider/venues/${venueId}/games`);
  },
  importGame(venueId: string, payload: ImportVenueGameInput) {
    return apiRequest<VenueGameRecord>(`/api/v1/provider/venues/${venueId}/games/import`, {
      method: "POST",
      body: payload,
    });
  },
  createGame(venueId: string, payload: CreateVenueGameInput) {
    return apiRequest<VenueGameRecord>(`/api/v1/provider/venues/${venueId}/games`, {
      method: "POST",
      body: payload,
    });
  },
  updateGame(venueId: string, gameId: string, payload: Partial<CreateVenueGameInput>) {
    return apiRequest<VenueGameRecord>(`/api/v1/provider/venues/${venueId}/games/${gameId}`, {
      method: "PUT",
      body: payload,
    });
  },
  deleteGame(venueId: string, gameId: string) {
    return apiRequest<void>(`/api/v1/provider/venues/${venueId}/games/${gameId}`, {
      method: "DELETE",
    });
  },

  // Food menu
  listFood(venueId: string) {
    return apiRequest<{ items: FoodItemRecord[] }>(`/api/v1/provider/venues/${venueId}/food`);
  },
  createFoodItem(venueId: string, payload: CreateFoodItemInput) {
    return apiRequest<FoodItemRecord>(`/api/v1/provider/venues/${venueId}/food`, {
      method: "POST",
      body: payload,
    });
  },
  updateFoodItem(venueId: string, itemId: string, payload: UpdateFoodItemInput) {
    return apiRequest<FoodItemRecord>(`/api/v1/provider/venues/${venueId}/food/${itemId}`, {
      method: "PUT",
      body: payload,
    });
  },
  deleteFoodItem(venueId: string, itemId: string) {
    return apiRequest<void>(`/api/v1/provider/venues/${venueId}/food/${itemId}`, {
      method: "DELETE",
    });
  },
  adjustFoodStock(
    venueId: string,
    itemId: string,
    payload: { kind: FoodStockAdjustmentKind; quantity: number; note?: string | null },
  ) {
    return apiRequest<FoodItemRecord>(`/api/v1/provider/venues/${venueId}/food/${itemId}/stock`, {
      method: "POST",
      body: payload,
    });
  },
  listFoodStockMovements(venueId: string, filters?: { itemId?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (filters?.itemId) params.set("item_id", filters.itemId);
    if (filters?.limit) params.set("limit", String(filters.limit));
    const query = params.toString() ? `?${params.toString()}` : "";
    return apiRequest<{ movements: FoodStockMovementRecord[] }>(
      `/api/v1/provider/venues/${venueId}/food-stock/movements${query}`,
      { disableOfflineFallback: true },
    );
  },
  listFoodOrders(
    filters?: { sessionId?: string; venueId?: string; statuses?: ProviderFoodOrderStatus[]; sinceHours?: number },
    options?: ProviderReadOptions,
  ) {
    const params = new URLSearchParams();

    if (filters?.sessionId) {
      params.set("session_id", filters.sessionId);
    }

    if (filters?.venueId) {
      params.set("venue_id", filters.venueId);
    }

    for (const status of filters?.statuses ?? []) {
      params.append("statuses", status);
    }

    if (filters?.sinceHours) {
      params.set("since_hours", String(filters.sinceHours));
    }

    const queryString = params.toString();
    const query = queryString ? `?${queryString}` : "";

    return apiRequest<{ orders: ProviderFoodOrder[] }>(`/api/v1/provider/food-orders${query}`, {
      disableOfflineFallback: options?.networkOnly,
    });
  },
  createCounterSale(payload: CreateCounterSaleInput) {
    return apiRequest<{ order: ProviderFoodOrder; receipt: ReceiptRecord }>("/api/v1/provider/counter-sales", {
      method: "POST",
      body: payload,
    });
  },
  createFoodOrder(payload: CreateProviderFoodOrderInput) {
    return apiRequest<ProviderFoodOrder>("/api/v1/provider/food-orders", {
      method: "POST",
      body: payload,
    });
  },
  updateFoodOrderStatus(orderId: string, status: ProviderFoodOrderStatus, cancelReason?: string | null) {
    return apiRequest<ProviderFoodOrder>(`/api/v1/provider/food-orders/${orderId}/status`, {
      method: "PUT",
      body: cancelReason ? { status, cancel_reason: cancelReason } : { status },
    });
  },
  createPresignedUpload(payload: CreatePresignedUploadInput) {
    return apiRequest<PresignedUploadResponse>("/api/v1/provider/uploads/presign", {
      method: "POST",
      body: payload,
    });
  },
  createVenuePhoto(venueId: string, payload: CreateProviderPhotoInput) {
    return apiRequest<ProviderVenue>(`/api/v1/provider/venues/${venueId}/photos`, {
      method: "POST",
      body: payload,
    });
  },
  updateVenuePhoto(venueId: string, photoId: string, payload: UpdateProviderPhotoInput) {
    return apiRequest<ProviderVenue>(`/api/v1/provider/venues/${venueId}/photos/${photoId}`, {
      method: "PATCH",
      body: payload,
    });
  },
  deleteVenuePhoto(venueId: string, photoId: string) {
    return apiRequest<ProviderVenue>(`/api/v1/provider/venues/${venueId}/photos/${photoId}`, {
      method: "DELETE",
    });
  },
  createUnitPhoto(venueId: string, unitId: string, payload: CreateProviderPhotoInput) {
    return apiRequest<ProviderUnit>(`/api/v1/provider/venues/${venueId}/units/${unitId}/photos`, {
      method: "POST",
      body: payload,
    });
  },
  updateUnitPhoto(venueId: string, unitId: string, photoId: string, payload: UpdateProviderPhotoInput) {
    return apiRequest<ProviderUnit>(`/api/v1/provider/venues/${venueId}/units/${unitId}/photos/${photoId}`, {
      method: "PATCH",
      body: payload,
    });
  },
  deleteUnitPhoto(venueId: string, unitId: string, photoId: string) {
    return apiRequest<ProviderUnit>(`/api/v1/provider/venues/${venueId}/units/${unitId}/photos/${photoId}`, {
      method: "DELETE",
    });
  },
  // Provider settings
  getBusinessProfile() {
    return apiRequest<ProviderBusinessProfileRecord>("/api/v1/provider/settings/business");
  },
  updateBusinessProfile(payload: Partial<ProviderBusinessProfileRecord>) {
    return apiRequest<ProviderBusinessProfileRecord>("/api/v1/provider/settings/business", {
      method: "PUT",
      body: payload,
    });
  },
  getVenueSettings(venueId: string) {
    return apiRequest<VenueSettingsRecord>(`/api/v1/provider/settings/venues/${venueId}`);
  },
  updateVenueSettings(venueId: string, payload: Partial<VenueSettingsRecord>) {
    return apiRequest<VenueSettingsRecord>(`/api/v1/provider/settings/venues/${venueId}`, {
      method: "PUT",
      body: payload,
    });
  },
  getSessionReceiptPreview(sessionId: string) {
    return apiRequest<SessionReceiptPreviewRecord>(`/api/v1/provider/sessions/${sessionId}/receipt-preview`);
  },
  issueSessionReceipt(sessionId: string, payload: IssueSessionReceiptInput) {
    return apiRequest<ReceiptRecord>(`/api/v1/provider/sessions/${sessionId}/receipt`, {
      method: "POST",
      body: payload,
    });
  },
  listReceipts(params: { search?: string; statusFilter?: string; venueId?: string; dateFrom?: string; dateTo?: string } = {}) {
    const searchParams = new URLSearchParams();
    if (params.venueId) {
      searchParams.set("venue_id", params.venueId);
    }
    if (params.search) {
      searchParams.set("search", params.search);
    }
    if (params.statusFilter) {
      searchParams.set("status_filter", params.statusFilter);
    }
    if (params.dateFrom) {
      searchParams.set("date_from", params.dateFrom);
    }
    if (params.dateTo) {
      searchParams.set("date_to", params.dateTo);
    }
    const query = searchParams.toString();
    return apiRequest<ReceiptListResponse>(`/api/v1/provider/receipts${query ? `?${query}` : ""}`);
  },
  getReceipt(receiptId: string) {
    return apiRequest<ReceiptRecord>(`/api/v1/provider/receipts/${receiptId}`);
  },
  recordReceiptPrint(receiptId: string, payload: RecordReceiptPrintInput = {}) {
    return apiRequest<ReceiptRecord>(`/api/v1/provider/receipts/${receiptId}/print`, {
      method: "POST",
      body: payload,
    });
  },
  recordReceiptPayment(receiptId: string, payload: RecordReceiptPaymentInput) {
    return apiRequest<ReceiptRecord>(`/api/v1/provider/receipts/${receiptId}/payments`, {
      method: "POST",
      body: payload,
    });
  },
  recordReceiptRefund(receiptId: string, payload: RecordReceiptRefundInput) {
    return apiRequest<ReceiptRecord>(`/api/v1/provider/receipts/${receiptId}/refunds`, {
      method: "POST",
      body: payload,
    });
  },
  voidReceipt(receiptId: string, payload: VoidReceiptInput) {
    return apiRequest<ReceiptRecord>(`/api/v1/provider/receipts/${receiptId}/void`, {
      method: "POST",
      body: payload,
    });
  },
  getCashDrawerSummary(venueId: string) {
    const searchParams = new URLSearchParams({ venue_id: venueId });
    return apiRequest<CashDrawerSummaryRecord>(`/api/v1/provider/cash-drawer?${searchParams.toString()}`);
  },
  openCashDrawer(payload: OpenCashDrawerInput) {
    return apiRequest<CashDrawerSummaryRecord>("/api/v1/provider/cash-drawer/open", {
      method: "POST",
      body: payload,
    });
  },
  closeCashDrawer(drawerId: string, payload: CloseCashDrawerInput) {
    return apiRequest<CashDrawerSummaryRecord>(`/api/v1/provider/cash-drawer/${drawerId}/close`, {
      method: "POST",
      body: payload,
    });
  },
  getDailyCloseoutReport(params: { venueId: string; dateLocal?: string }) {
    const searchParams = new URLSearchParams({ venue_id: params.venueId });
    if (params.dateLocal) {
      searchParams.set("date_local", params.dateLocal);
    }
    return apiRequest<DailyCloseoutReportRecord>(`/api/v1/provider/reports/daily-closeout?${searchParams.toString()}`);
  },
  getPolicy() {
    return apiRequest<ProviderPolicyRecord>("/api/v1/provider/settings/policy");
  },
  updatePolicy(payload: Partial<ProviderPolicyRecord>) {
    return apiRequest<ProviderPolicyRecord>("/api/v1/provider/settings/policy", {
      method: "PUT",
      body: payload,
    });
  },
};

export const customerApi = {
  listBookings() {
    return apiRequest<CustomerBookingsResponse>("/api/v1/customer/bookings");
  },
  listActiveSessions() {
    return apiRequest<CustomerSessionsResponse>("/api/v1/customer/sessions/active");
  },
  listSessionFoodOrders(sessionId: string) {
    return apiRequest<CustomerFoodOrdersResponse>(
      `/api/v1/customer/sessions/${sessionId}/food-orders`,
    );
  },
};

export function requestAccountDeletion(payload: { reason?: string }) {
  return apiRequest<AccountDeletionRequestResponse>(
    "/api/v1/customer/account-deletion-request",
    { method: "POST", body: payload },
  );
}

export function listAccountDeletionRequests(status?: AccountDeletionRequestStatus) {
  const query = status && status !== "all" ? `?status=${status}` : "";
  return apiRequest<DeletionRequestListItem[]>(
    `/api/v1/admin/account-deletion-requests${query}`,
    { method: "GET" },
  );
}

export function approveAccountDeletionRequest(requestId: string, adminNote?: string) {
  return apiRequest<DeletionRequestListItem>(
    `/api/v1/admin/account-deletion-requests/${requestId}/approve`,
    { method: "POST", body: { admin_note: adminNote } },
  );
}

export function rejectAccountDeletionRequest(requestId: string, adminNote: string) {
  return apiRequest<DeletionRequestListItem>(
    `/api/v1/admin/account-deletion-requests/${requestId}/reject`,
    { method: "POST", body: { admin_note: adminNote } },
  );
}


export const assistanceApi = {
  start(provider_id: string, reason: string) {
    return apiRequest<AssistanceSession & SessionResponse>("/api/v1/admin/assistance", { method: "POST", body: { provider_id, reason } });
  },
  current() {
    return apiRequest<Omit<AssistanceSession, "token"> & SessionResponse>("/api/v1/admin/assistance/current");
  },
  end() { return apiRequest<void>("/api/v1/admin/assistance/end", { method: "POST" }); },
};

export interface PlatformTeamUser {
  id: string;
  email: string;
  display_name: string;
  role: "admin" | "support";
  is_active: boolean;
  setup_pending: boolean;
}
export const platformTeamApi = {
  list() { return apiRequest<{ users: PlatformTeamUser[] }>("/api/v1/admin/team"); },
  invite(body: { email: string; display_name: string; role: "admin" | "support" }) {
    return apiRequest<{ user: PlatformTeamUser; email_sent: boolean }>("/api/v1/admin/team", { method: "POST", body });
  },
  update(user: PlatformTeamUser, changes: { role: "admin" | "support"; is_active: boolean }) {
    return apiRequest<PlatformTeamUser>(`/api/v1/admin/team/${user.id}`, { method: "PATCH", body: changes });
  },
  resend(id: string) {
    return apiRequest<{ user: PlatformTeamUser; email_sent: boolean }>(`/api/v1/admin/team/${id}/invite`, { method: "POST" });
  },
};
